/**
 * The export menu every platform shows in the view widget of the room editor
 * and of the floor plan view: its items in order (the 2D plan formats, the 3D
 * model formats, then, where the menu has JSON, JSON to download, copy and,
 * in the room editor, paste), what Copy JSON and Paste JSON say, and the JSON
 * each editor offers, with the room editor's paste applied as one undo step,
 * and each editor's reset item, last.
 * A platform supplies the clipboard, the file delivery and, where it draws
 * them, the 2D plan formats.
 */
import { type FloorPlanAssemblerState, type FloorPlanEditorState, resetRoom } from '../react';
import { type ExportFormat, MODEL_EXPORT_FORMATS, PLAN_EXPORT_FORMATS, ROOM_EXPORT_NAME, ROOMS_EXPORT_NAME } from './exportFiles';
import {
  assemblyRoomsJson,
  type DescribeAssemblyRoom,
  type JsonPasteOutcome,
  looksLikeJson,
  pasteRoomJson,
  roomJson,
} from './floorPlanJson';

/** The reset item each editor ends its menu with: the room editor resets the room, the floor plan view resets the room layout. */
export type ExportMenuResetId = 'reset-room' | 'reset-layout';

const RESET_LABELS: Record<ExportMenuResetId, string> = { 'reset-room': 'Reset this room', 'reset-layout': 'Reset room layout' };

export type ExportMenuItemId<Format extends ExportFormat = ExportFormat> = Format | 'download-json' | 'copy-json' | 'paste-json' | ExportMenuResetId;

export interface ExportMenuItem<Format extends ExportFormat = ExportFormat> {
  id: ExportMenuItemId<Format>;
  label: string;
}

export interface ExportMenuGroup<Format extends ExportFormat = ExportFormat> {
  label: string;
  items: ExportMenuItem<Format>[];
}

const FORMAT_GROUPS: [string, readonly ExportFormat[]][] = [
  ['2D', PLAN_EXPORT_FORMATS],
  ['3D', MODEL_EXPORT_FORMATS],
];

/**
 * The menu's groups and items, in order: the `formats` the platform offers
 * under 2D and 3D, then, when the menu has `json`, the JSON items, with Paste
 * JSON only where a paste can change something (`paste`, false when read-only
 * or when the menu has no paste). A `reset` item gets a group of its own at
 * the very bottom.
 */
export function exportMenuGroups<Format extends ExportFormat>(
  { formats, json, paste, reset }: { formats: readonly Format[]; json: boolean; paste: boolean; reset?: ExportMenuResetId },
): ExportMenuGroup<Format>[] {
  const formatGroups = FORMAT_GROUPS.map(([label, groupFormats]) => ({
    label,
    items: formats.filter(format => groupFormats.includes(format)).map(format => ({ id: format, label: format.toUpperCase() })),
  }));
  const jsonItems: ExportMenuItem<Format>[] = [
    { id: 'download-json', label: 'Download JSON' },
    { id: 'copy-json', label: 'Copy JSON' },
    ...(paste ? [{ id: 'paste-json' as const, label: 'Paste JSON' }] : []),
  ];
  return [
    ...formatGroups.filter(group => group.items.length > 0),
    ...(json ? [{ label: 'JSON', items: jsonItems }] : []),
    ...(reset ? [{ label: 'Reset', items: [{ id: reset, label: RESET_LABELS[reset] }] }] : []),
  ];
}

/** What Copy JSON says, given whether the clipboard took the text. */
export const copyJsonOutcome = (copied: boolean): JsonPasteOutcome =>
  copied ? { ok: true, message: 'Copied to the clipboard.' } : { ok: false, message: "Couldn't write to the clipboard." };

/** What Paste JSON does with the clipboard's text (null when it couldn't be read): `pasteJson` applies it, unless it isn't JSON at all. */
export const pasteJsonOutcome = (clipboard: string | null, pasteJson: (text: string) => JsonPasteOutcome): JsonPasteOutcome => {
  if (clipboard === null) return { ok: false, message: "Couldn't read the clipboard." };
  if (!looksLikeJson(clipboard)) return { ok: false, message: "The clipboard doesn't hold JSON. Nothing was changed." };
  return pasteJson(clipboard);
};

/** The JSON half of an export menu: what Download JSON saves and Copy JSON copies, and, where the menu pastes, what Paste JSON applies. */
export interface ExportMenuJson {
  /** The JSON that Download JSON saves and Copy JSON copies, as it is when picked. */
  json: () => string;
  /** The downloaded JSON file's name, without `.json`. */
  jsonFileName: string;
  /** Applies JSON from the clipboard, or says why not; absent where the menu has no paste or nothing can change (read-only). */
  pasteJson?: (text: string) => JsonPasteOutcome;
}

/** The room editor's JSON: the room, and a pasted room replacing it as one undo step. */
export const roomExportMenuJson = (floorPlanEditorState: FloorPlanEditorState): ExportMenuJson => ({
  json: () => roomJson(floorPlanEditorState.stRef.current.shape),
  jsonFileName: ROOM_EXPORT_NAME,
  pasteJson: floorPlanEditorState.st.readOnly ? undefined : text => {
    const { shape, ...outcome } = pasteRoomJson(floorPlanEditorState.stRef.current.shape, text);
    if (shape) floorPlanEditorState.setShape(shape);
    return outcome;
  },
});

/** The floor plan view's JSON: every room on every level, to download or copy. */
export const assemblyExportMenuJson = (floorPlanAssemblerState: FloorPlanAssemblerState, describeRoom?: DescribeAssemblyRoom): ExportMenuJson => ({
  json: () => assemblyRoomsJson(floorPlanAssemblerState.stRef.current.rooms, describeRoom),
  jsonFileName: ROOMS_EXPORT_NAME,
});

/** The reset half of an export menu: which reset item it ends with, and what picking it does. */
export interface ExportMenuReset {
  id: ExportMenuResetId;
  reset: () => void;
}

/** The room editor's reset: the room replaced by the default room as one undo step; absent when read-only. */
export const roomExportMenuReset = (floorPlanEditorState: FloorPlanEditorState): ExportMenuReset | undefined =>
  floorPlanEditorState.st.readOnly ? undefined : { id: 'reset-room', reset: () => resetRoom(floorPlanEditorState) };

/** The floor plan view's reset: every room back to the automatic initial placement as one undo step; absent when read-only. */
export const assemblyExportMenuReset = (floorPlanAssemblerState: FloorPlanAssemblerState): ExportMenuReset | undefined =>
  floorPlanAssemblerState.st.readOnly ? undefined : { id: 'reset-layout', reset: floorPlanAssemblerState.resetLayout };
